/**
 * LoginRequiredRouter: Handle routing to a page based on if the user is
 * logged-in or not. Can be configured to also allow in Phantom Users or
 * disallow access to Child Users.
 *
 * If a disallowed child account attempts to access the page then the user will
 * see an error message stating they're not allowed to access it.
 *
 * If the user is logged out, or is a disallowed phantom user, they will be
 * redirected to the login page (and they will be returned upon a successful
 * login attempt).
 */
import * as React from "react";
import {Route} from "react-router-dom";

import LoginRequired from "@repo/component-screens/login-required";
import {PermissionsInfo, RequestInfo} from "@repo/shared-core";

import withUserProfile, {
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import AsyncRouteHandler from "./async-route-handler.tsx";
import PermissionDenied from "./errors/permission-denied.tsx";
import RouteLoadSpinner from "./route-load-spinner.tsx";
import type {CommonRouteProps} from "./types.ts";

type ChildAllowedProps = {
    children: React.ReactNode;
    childUserAllowed: boolean;
    template?: CommonRouteProps["template"];
} & WithUserProfileProps;

/**
 * The only way we know for certain that a user is a child is to check
 * the isChildAccount property on the user's profile object.
 */
const ChildAllowed = withUserProfile(
    ({children, profile, childUserAllowed, template}: ChildAllowedProps) => {
        if (childUserAllowed) {
            return children;
        }

        if (profile) {
            return profile?.get("isChildAccount") ? (
                <PermissionDenied />
            ) : (
                children
            );
        }

        return <RouteLoadSpinner template={template} />;
    },
);

type DefaultProps = {
    // Allow Phantom users to be considered logged-in, or not.
    // Defaults to: false
    phantomUserAllowed: boolean;
    // Allow Child users to be considered logged-in, or not.
    // Defaults to: true
    childUserAllowed: boolean;
    // Allow mobile app users to bypass login for special cases.
    // Defaults to: false
    mobileAppUserAllowed: boolean;
};

type Props = DefaultProps & CommonRouteProps;

export default class LoginRequiredRoute extends React.Component<Props> {
    static defaultProps: DefaultProps = {
        phantomUserAllowed: false,
        childUserAllowed: true,
        mobileAppUserAllowed: false,
    };

    isAllowedToView(): boolean {
        return (
            (this.props.phantomUserAllowed && PermissionsInfo.isPhantom) ||
            (this.props.mobileAppUserAllowed && RequestInfo.isMobileApp)
        );
    }

    renderRoute(): React.ReactNode {
        return (
            <AsyncRouteHandler
                component={this.props.component}
                template={this.props.template}
            />
        );
    }

    render(): React.ReactNode {
        return (
            <Route
                path={this.props.path}
                exact={this.props.exact}
                render={() => {
                    if (this.isAllowedToView()) {
                        return this.renderRoute();
                    }

                    if (PermissionsInfo.isLoggedIn) {
                        return (
                            <ChildAllowed
                                childUserAllowed={this.props.childUserAllowed}
                                template={this.props.template}
                            >
                                {this.renderRoute()}
                            </ChildAllowed>
                        );
                    }

                    // If the user is logged out, or a phantom user when
                    // disallowed, then we redirect them to the login page
                    // (which will redirect them back to this page after a
                    // successful login)
                    return <LoginRequired />;
                }}
            />
        );
    }
}
